import { useMemo } from 'react'

import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { getAutomationCapabilities } from 'api/Cerebro/orgScoped/automationsApi'
import { getBrandGroups } from 'api/Cerebro/orgScoped/brandGroupsApi'
import { getOrganization } from 'api/Cerebro/orgScoped/organizationApi'
import { PaginatedResponse } from 'api/Cerebro/types'
import { getPrintableDashboard } from 'api/Cerebro/unscoped/resourceApi'
import { useCerebroApiRequest } from 'hooks'
import {
    AutomationCapability,
    BrandGroup,
    Dashboard,
    Organization,
} from 'types'

/**
 * Override scope for the print page: unlike the ambient `useOrgContext` scope
 * used by in-app detail queries, these come from the print URL params so the
 * headless PDF render impersonates the correct user/org (FR-C1).
 */
interface PrintOverrideScope {
    userId?: string | null
    orgId?: string | null
    orgGroupId?: string | null
}

const DASHBOARD_PRINT_QUERY_ROOT = 'dashboardPrint' as const

export function usePrintableDashboardQuery({
    dashboardId,
    userId,
    orgId,
    orgGroupId,
}: PrintOverrideScope & {
    dashboardId?: string
}): UseQueryResult<Dashboard | null, Error> & { queryKey: unknown[] } {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const queryKey = useMemo(
        () => [
            DASHBOARD_PRINT_QUERY_ROOT,
            'dashboard',
            dashboardId,
            userId ?? null,
            orgId ?? null,
            orgGroupId ?? null,
        ],
        [dashboardId, userId, orgId, orgGroupId]
    )

    const result = useQuery({
        queryKey,
        queryFn: async (): Promise<Dashboard | null> => {
            const response = await makeCerebroApiRequest({
                request: getPrintableDashboard(dashboardId!, {
                    headers: { userId, orgId, orgGroupId },
                }),
                suppressErrorAlerts: true,
            })
            return response?.data ?? null
        },
        enabled: !!dashboardId,
    })

    return { ...result, queryKey }
}

export function usePrintOrganizationQuery({
    organizationId,
    userId,
    orgGroupId,
    enabled = true,
}: Pick<PrintOverrideScope, 'userId' | 'orgGroupId'> & {
    organizationId?: string
    enabled?: boolean
}): UseQueryResult<Organization | null, Error> & { queryKey: unknown[] } {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const queryKey = useMemo(
        () => [
            DASHBOARD_PRINT_QUERY_ROOT,
            'organization',
            organizationId,
            userId ?? null,
            orgGroupId ?? null,
        ],
        [organizationId, userId, orgGroupId]
    )

    const result = useQuery({
        queryKey,
        queryFn: async (): Promise<Organization | null> => {
            const response = await makeCerebroApiRequest({
                request: getOrganization(organizationId!, {
                    headers: { userId, orgGroupId },
                }),
                suppressErrorAlerts: true,
            })
            return response?.data ?? null
        },
        enabled: !!organizationId && enabled,
    })

    return { ...result, queryKey }
}

export function usePrintBrandGroupsQuery({
    userId,
    orgId,
    orgGroupId,
}: PrintOverrideScope): UseQueryResult<
    PaginatedResponse<BrandGroup> | null,
    Error
> & { queryKey: unknown[] } {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const queryKey = useMemo(
        () => [
            DASHBOARD_PRINT_QUERY_ROOT,
            'brandGroups',
            userId ?? null,
            orgId ?? null,
            orgGroupId ?? null,
        ],
        [userId, orgId, orgGroupId]
    )

    const result = useQuery({
        queryKey,
        queryFn: async (): Promise<PaginatedResponse<BrandGroup> | null> => {
            const response = await makeCerebroApiRequest({
                request: getBrandGroups(
                    {},
                    { headers: { noCount: true, userId, orgId, orgGroupId } }
                ),
                suppressErrorAlerts: true,
            })
            return response?.data ?? null
        },
    })

    return { ...result, queryKey }
}

export function usePrintAutomationCapabilitiesQuery({
    userId,
    orgId,
    orgGroupId,
}: PrintOverrideScope): UseQueryResult<
    PaginatedResponse<AutomationCapability> | null,
    Error
> & { queryKey: unknown[] } {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })

    const queryKey = useMemo(
        () => [
            DASHBOARD_PRINT_QUERY_ROOT,
            'automationCapabilities',
            userId ?? null,
            orgId ?? null,
            orgGroupId ?? null,
        ],
        [userId, orgId, orgGroupId]
    )

    const result = useQuery({
        queryKey,
        queryFn:
            async (): Promise<PaginatedResponse<AutomationCapability> | null> => {
                const response = await makeCerebroApiRequest({
                    request: getAutomationCapabilities(
                        {},
                        {
                            headers: {
                                noCount: true,
                                userId,
                                orgId,
                                orgGroupId,
                            },
                        }
                    ),
                    suppressErrorAlerts: true,
                })
                return response?.data ?? null
            },
    })

    return { ...result, queryKey }
}
